import { useState, type CSSProperties } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import DashStatCard from '../../components/ui/DashStatCard'
import DashIcon from '../../components/ui/DashIcon'
import DashAvatar from '../../components/ui/DashAvatar'
import StatusBadge from '../../components/ui/StatusBadge'
import AddCommandeModal from '../../components/expediteur/AddCommandeModal'
import { ImportCSVModal } from '../../components/expediteur/ImportCSVModal'
import { NotificationBell } from '../../components/shared/NotificationBell'
import { useExpediteurStats, type StatsPeriod } from '../../hooks/useExpediteurStats'
import { useDeliveries } from '../../hooks/useDeliveries'
import { usersAPI } from '../../api/users'
import type { Delivery } from '../../types'

const PERIOD_LABELS: Record<StatsPeriod, string> = {
  week:  'Cette semaine',
  month: 'Ce mois-ci',
  year:  'Cette année',
}

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })

function TimelineBar({ data, loading }: {
  data: Array<{ _id: string; total: number; delivered: number; revenue: number }>
  loading: boolean
}) {
  const max = Math.max(...data.map(d => d.total), 1)
  return (
    <div className="card chart-card">
      <div className="chart-head">
        <h3 style={{ margin: 0, fontSize: '.9rem', fontWeight: 700 }}>Activité — 7 derniers jours</h3>
      </div>
      <div className="chart-body" style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 140, paddingTop: 8 }}>
        {loading ? (
          <div style={{ color: 'var(--muted)', fontSize: '.82rem', margin: 'auto' }}>Chargement…</div>
        ) : data.length === 0 ? (
          <div style={{ color: 'var(--muted)', fontSize: '.82rem', margin: 'auto' }}>Aucune donnée sur cette période</div>
        ) : data.map((point) => (
          <div key={point._id} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <div
              title={`${point.delivered} livrés / ${point.total} total`}
              style={{
                width: '100%',
                height: `${(point.delivered / max) * 110}px`,
                background: 'var(--green-600, #1D9E75)',
                borderRadius: '4px 4px 0 0',
                minHeight: 4,
                transition: 'height 0.3s ease',
              }}
            />
            <span style={{ fontSize: 10, color: 'var(--muted)' }}>
              {new Date(point._id).toLocaleDateString('fr-FR', { weekday: 'short' })}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

function StatusBreakdown({ stats, loading }: {
  stats?: { delivered: number; inTransit: number; failed: number; pending: number; total: number }
  loading: boolean
}) {
  const total = stats?.total ?? 0
  const pct = (n: number) => (total > 0 ? Math.round((n / total) * 100) : 0)

  const rows = [
    { label: 'Livrés',     value: stats?.delivered ?? 0, color: '#10B981' },
    { label: 'En cours',   value: stats?.inTransit ?? 0, color: '#3B82F6' },
    { label: 'Échoués',    value: stats?.failed ?? 0,    color: '#EF4444' },
    { label: 'En attente', value: stats?.pending ?? 0,   color: '#9CA3AF' },
  ]

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div className="card-head">
        <h3>Répartition</h3>
        <span style={{ fontSize: '.78rem', color: 'var(--muted)' }}>Période sélectionnée</span>
      </div>
      {loading ? (
        <div style={{ color: 'var(--muted)', fontSize: '.82rem' }}>Chargement…</div>
      ) : rows.map((row) => (
        <div key={row.label}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.82rem', marginBottom: 4 }}>
            <span style={{ color: 'var(--navy-800)' }}>{row.label}</span>
            <span style={{ fontWeight: 700 }}>
              {row.value} <span style={{ color: 'var(--muted)', fontWeight: 400 }}>({pct(row.value)}%)</span>
            </span>
          </div>
          <div style={{ background: 'var(--line)', borderRadius: 4, height: 6, overflow: 'hidden' }}>
            <div style={{ width: `${pct(row.value)}%`, height: '100%', background: row.color, borderRadius: 4, transition: 'width 0.5s ease' }} />
          </div>
        </div>
      ))}
    </div>
  )
}

function RecentTable({ deliveries, loading, onDetails }: {
  deliveries: Delivery[]
  loading: boolean
  onDetails: (id: string) => void
}) {
  if (loading) {
    return <div style={{ padding: '20px', color: 'var(--muted)', fontSize: '.82rem' }}>Chargement…</div>
  }
  if (deliveries.length === 0) {
    return <div style={{ padding: '20px', color: 'var(--muted)', fontSize: '.82rem', textAlign: 'center' }}>Aucun colis récent</div>
  }
  return (
    <div className="tbl-scroll">
      <table className="tbl" style={{ fontSize: '.84rem' }}>
        <thead>
          <tr><th>Colis</th><th>Destinataire</th><th>Adresse</th><th>Statut</th><th>COD</th><th>Date</th><th className="col-actions">Actions</th></tr>
        </thead>
        <tbody>
          {deliveries.map(d => (
            <tr key={d._id}>
              <td><span className="row-id">{d.trackingCode}</span></td>
              <td>
                <div className="cell-user">
                  <DashAvatar name={d.dropoff.contactName ?? '?'} size={28} />
                  <div>
                    <b>{d.dropoff.contactName ?? '—'}</b>
                    <span>{d.dropoff.contactPhone ?? ''}</span>
                  </div>
                </div>
              </td>
              <td style={{ maxWidth: 180 }}>
                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {d.dropoff.address}
                </div>
              </td>
              <td><StatusBadge status={d.status} /></td>
              <td className="mono row-strong">{d.price?.base ?? 0} TND</td>
              <td className="mono" style={{ color: 'var(--muted)' }}>{formatDate(d.createdAt)}</td>
              <td className="col-actions">
                <button onClick={() => onDetails(d._id)}>Détails</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default function ExpediteurDashboard() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [period, setPeriod] = useState<StatsPeriod>('month')
  const [modalOpen, setModalOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)

  const { data: user } = useQuery({
    queryKey: ['me'],
    queryFn: usersAPI.getMe,
    staleTime: Infinity,
  })

  const { data: stats, isLoading: statsLoading } = useExpediteurStats(period)
  const { data: recentData, isLoading: recentLoading } = useDeliveries({ limit: 5 })

  const displayName = (user as { company?: { name?: string } } | undefined)?.company?.name ?? user?.name ?? '—'

  return (
    <>
      {modalOpen && <AddCommandeModal onClose={() => setModalOpen(false)} />}
      <ImportCSVModal
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        onSuccess={(count) => {
          toast.success(`${count} colis importés avec succès !`)
          queryClient.invalidateQueries({ queryKey: ['deliveries'] })
          queryClient.invalidateQueries({ queryKey: ['expediteur-stats'] })
        }}
      />

      <div className="page-head">
        <div className="ph-titles">
          <h1>Bonjour, {displayName} 👋</h1>
          <p>Aperçu de votre activité · {PERIOD_LABELS[period]}</p>
        </div>
        <div className="ph-actions">
          {/* Sélecteur de période */}
          <div style={{ display: 'flex', gap: 4, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 8, padding: 3 }}>
            {(['week', 'month', 'year'] as StatsPeriod[]).map(p => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                style={{
                  padding: '4px 12px',
                  borderRadius: 6,
                  border: 'none',
                  fontSize: '.78rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  background: period === p ? 'var(--green-700, #1D9E75)' : 'transparent',
                  color: period === p ? '#fff' : 'var(--muted)',
                  transition: 'all 0.15s',
                }}
              >
                {p === 'week' ? '7j' : p === 'month' ? '30j' : '1a'}
              </button>
            ))}
          </div>
          <NotificationBell />
          <button className="btn btn-ghost" onClick={() => setImportOpen(true)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 6 }}>
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="12" y1="18" x2="12" y2="12"/>
              <line x1="9" y1="15" x2="15" y2="15"/>
            </svg>
            Importer CSV
          </button>
          <button className="btn btn-primary" onClick={() => setModalOpen(true)}>
            <DashIcon name="plus" size={14} /> Nouveau colis
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="stat-grid stat-grid-4" style={{ marginBottom: 18 }}>
        <DashStatCard
          label="Total colis"
          value={statsLoading ? '…' : String(stats?.parcels.total ?? 0)}
          trend={`Taux ${stats?.parcels.successRate ?? 0}%`}
          icon="package"
        />
        <DashStatCard
          label="Livrés"
          value={statsLoading ? '…' : String(stats?.parcels.delivered ?? 0)}
          trend={`${stats?.parcels.successRate ?? 0}% succès`}
          icon="check"
          accent="green"
        />
        <DashStatCard
          label="En cours"
          value={statsLoading ? '…' : String(stats?.parcels.inTransit ?? 0)}
          trend={`+ ${stats?.parcels.pending ?? 0} en attente`}
          icon="truck"
        />
        <DashStatCard
          label="Dépensé"
          value={statsLoading ? '…' : `${stats?.spending.total ?? 0} TND`}
          trend={`Moy. ${stats?.spending.average ?? 0} TND`}
          icon="wallet"
        />
      </div>

      {/* Timeline + Répartition */}
      <div className="detail-grid" style={{ '--detail-cols': '1.5fr 1fr', gap: 18, marginBottom: 18 } as CSSProperties}>
        <TimelineBar data={stats?.timeline ?? []} loading={statsLoading} />
        <StatusBreakdown stats={stats?.parcels} loading={statsLoading} />
      </div>

      {/* Derniers colis */}
      <div className="card">
        <div className="card-head">
          <h3>Derniers colis</h3>
          <button className="btn btn-sm btn-ghost" onClick={() => navigate('/expediteur/livraisons')}>
            Voir tout →
          </button>
        </div>
        <RecentTable
          deliveries={recentData?.data ?? []}
          loading={recentLoading}
          onDetails={(id) => navigate(`/expediteur/colis/${id}`)}
        />
      </div>
    </>
  )
}
